import { useRef, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";

// Voicemail / video-message concern for calls that go unanswered, extracted from
// CallContext. Independent of the peer connection — it records a fresh MediaStream,
// uploads it and posts it as a DM. Shares only the local <video> element ref (for
// the recording preview) with usePeerConnection.
//
// peerRef / mediaRef are the same refs the call state machine owns; onSent is called
// after a successful send or a skip so CallContext can run its cleanup.
export function useVoicemail({ peerRef, mediaRef, localVideoRef, onSent }) {
  const [recording, setRecording] = useState(false);
  const [recorded, setRecorded] = useState(false);
  const [recSecs, setRecSecs] = useState(0);
  const [sendingVm, setSendingVm] = useState(false);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const recStreamRef = useRef(null);
  const recBlobRef = useRef(null);
  const recTimerRef = useRef(null);

  const stopRecord = useCallback(() => {
    clearInterval(recTimerRef.current);
    try { if (recRef.current && recRef.current.state !== "inactive") recRef.current.stop(); } catch (e) {}
    if (recStreamRef.current) { recStreamRef.current.getTracks().forEach((t) => t.stop()); recStreamRef.current = null; }
    setRecording(false);
  }, []);

  const startRecord = useCallback(async () => {
    const wantVideo = mediaRef.current === "video";
    let stream;
    try { stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: wantVideo ? { width: { ideal: 640 }, height: { ideal: 480 } } : false }); }
    catch (e) { toast.error("Couldn't access your " + (wantVideo ? "camera" : "microphone")); return; }
    recStreamRef.current = stream;
    setRecording(true); setRecorded(false); setRecSecs(0);
    setTimeout(() => { if (wantVideo && localVideoRef.current) localVideoRef.current.srcObject = stream; }, 60);
    chunksRef.current = [];
    let mr;
    // Pick a mime the browser actually supports (Safari/iOS lacks WebM). Prefer
    // audio/mp4/AAC first so the recording is broadly playable; fall back to WebM/Ogg.
    const audioCandidates = ["audio/mp4", "audio/aac", "audio/webm;codecs=opus", "audio/webm", "audio/ogg;codecs=opus"];
    const videoCandidates = ["video/mp4", "video/webm;codecs=vp8,opus", "video/webm"];
    const candidates = wantVideo ? videoCandidates : audioCandidates;
    const supported = (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported)
      ? candidates.find((t) => MediaRecorder.isTypeSupported(t)) : "";
    try { mr = supported ? new MediaRecorder(stream, { mimeType: supported }) : new MediaRecorder(stream); }
    catch (e) { mr = new MediaRecorder(stream); }
    mr.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
    mr.onstop = () => {
      const mime = (mr.mimeType || supported || (wantVideo ? "video/webm" : "audio/webm")).split(";")[0];
      recBlobRef.current = new Blob(chunksRef.current, { type: mime });
      setRecorded(true);
    };
    recRef.current = mr; mr.start();
    recTimerRef.current = setInterval(() => setRecSecs((s) => { if (s >= 119) { stopRecord(); } return s + 1; }), 1000);
  }, [stopRecord, mediaRef, localVideoRef]);

  const sendVoicemail = useCallback(async () => {
    const blob = recBlobRef.current;
    if (!blob || !peerRef.current) { onSent(); return; }
    const wantVideo = mediaRef.current === "video";
    setSendingVm(true);
    try {
      const mime = blob.type || (wantVideo ? "video/webm" : "audio/webm");
      const ext = wantVideo
        ? (mime.includes("mp4") ? "mp4" : "webm")
        : ((mime.includes("mp4") || mime.includes("aac") || mime.includes("m4a")) ? "m4a" : mime.includes("ogg") ? "ogg" : "webm");
      const fd = new FormData();
      fd.append("file", new File([blob], wantVideo ? `video-message.${ext}` : `voice-message.${ext}`, { type: mime }));
      const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post("/messages", {
        recipient_id: peerRef.current.id,
        content: wantVideo ? "🎥 Video message" : "🎙️ Voice message",
        file_url: up.data.url, file_name: wantVideo ? "Video message" : "Voice message",
      });
      toast.success(wantVideo ? "Video message sent" : "Voice message sent");
    } catch (e) { toast.error("Couldn't send your message — please try again"); }
    onSent();
  }, [onSent, peerRef, mediaRef]);

  // Tear down any in-flight recording + reset state (called from CallContext cleanup).
  const resetVoicemail = useCallback(() => {
    clearInterval(recTimerRef.current);
    try { if (recRef.current && recRef.current.state !== "inactive") recRef.current.stop(); } catch (e) {}
    if (recStreamRef.current) { recStreamRef.current.getTracks().forEach((t) => t.stop()); recStreamRef.current = null; }
    recRef.current = null; recBlobRef.current = null; chunksRef.current = [];
    setRecording(false); setRecorded(false); setRecSecs(0); setSendingVm(false);
  }, []);

  return { recording, recorded, recSecs, sendingVm, startRecord, stopRecord, sendVoicemail, resetVoicemail };
}
